import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import { RoomsService } from '../../services/rooms.service';
import { roomsActions } from '.';
import { ApiError } from '../../models/common/api-error';
import { concat, defer, EMPTY, Observable, of } from 'rxjs';
import { RoomsState } from './rooms.state';
import { storeRoomsKey } from '../reducers';
import { ServerRenderedDataService } from '../../services/server-rendered-data.service';

@Injectable({
  providedIn: 'root',
})
export class RoomsEffects {
  private serverRenderedData = inject(ServerRenderedDataService);

  constructor(
    private store$: Store,
    private actions$: Actions,
    private roomsService: RoomsService,
  ) {}

  needRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.needRoom),
      withLatestFrom(this.store$),
      filter(([action, store]) =>
        this.roomNeedsFetch(store[storeRoomsKey], action.id, action.includes),
      ),
      map(([action]) =>
        roomsActions.findOne({ id: action.id, includes: action.includes }),
      ),
    ),
  );

  findOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.findOne),
      mergeMap((action) =>
        concat(
          this.findRoom(action.id, action.includes).pipe(
            catchError((error: ApiError) =>
              of(roomsActions.findError({ error: error.message })),
            ),
          ),
          this.reloadRoomWithSession(action.id, action.includes),
        ),
      ),
    ),
  );

  private findRoom(
    id: number,
    includes?: string[],
    options: { ignoreServerRenderedResponse?: boolean } = {},
  ): Observable<Action> {
    return this.roomsService.findOne(id, includes, options).pipe(
      map((response) =>
        roomsActions.findOneSuccess({
          room: response.data,
          full: !includes,
          includes,
        }),
      ),
    );
  }

  private reloadRoomWithSession(
    id: number,
    includes?: string[],
  ): Observable<Action> {
    return defer(() =>
      this.serverRenderedData.mustReloadWithSession()
        ? this.findRoom(id, includes, {
            ignoreServerRenderedResponse: true,
          }).pipe(catchError(() => EMPTY))
        : EMPTY,
    );
  }

  private roomNeedsFetch(
    state: RoomsState,
    id: number,
    includes?: string[],
  ): boolean {
    const cachedRoom = state.entities[id];
    if (!cachedRoom) {
      return true;
    }
    if (!includes) {
      return !state.loadedFullById[id];
    }
    if (state.loadedFullById[id]) {
      return false;
    }
    const loadedIncludes = state.loadedIncludesById[id] ?? [];
    return !includes.every((include) => loadedIncludes.includes(include));
  }

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.findAll),
      withLatestFrom(this.store$),
      mergeMap(([action, store]) => {
        return this.roomsService.findAll(action.page).pipe(
          map((response) =>
            roomsActions.findAllSuccess({
              rooms: response.data,
              total: response.total,
            }),
          ),
          catchError((error: ApiError) =>
            of(roomsActions.findError({ error: error.message })),
          ),
        );
      }),
    ),
  );
}
